import React from 'react';

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { AxiosResponse } from 'axios';
import { FormProvider } from 'react-hook-form';

import { SendStaffInviteRequest, UpdateStaffPermissionsPayload } from '@vyce/core/src/api/types';
import { CompanyFilter } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { Company } from '@vyce/core/src/types';
import useInviteStyles from '@vyce/core/src/views/invites/styles';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { useAdminInviteDialog } from '../../hooks/useAdminInviteDialog';
import { AdminRoleStep } from './AdminRoleStep';
import { AdminWhoStep } from './AdminWhoStep';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save or invite — refresh the admins list */
  onSuccess: () => void;
  companyId: string | null;
  /** Legend adds admins to any company, so it passes the list to pick from */
  companies?: Company[];
  updateStaffPermissionsRequest: (
    companyId: string,
    userId: string,
    data: UpdateStaffPermissionsPayload
  ) => Promise<AxiosResponse<unknown>>;
  createStaffInviteRequest: (
    companyId: string,
    payload: SendStaffInviteRequest
  ) => Promise<AxiosResponse<unknown>>;
}

/**
 * Add an admin in two steps, laid out like the worker invite: who (a new email, or one
 * of the company's existing people), then their role in each module. Existing people
 * get the access immediately — a page refresh on their side picks it up.
 */
export const AdminInviteDialog: React.FC<Props> = ({ open, companies, ...props }) => {
  const classes = useInviteStyles();
  const dialog = useAdminInviteDialog({ open, ...props });
  const isWhoStep = dialog.step === 'who';

  let submitLabel = 'Send invite';
  if (dialog.isExisting) {
    submitLabel = dialog.staffItem ? 'Update permissions' : 'Save permissions';
  }

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      classes={{ paper: classes.dialogPaper }}
      fullScreen={dialog.isMobile}
      maxWidth="md"
      fullWidth
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          dialog.handleClose();
        }
      }}
    >
      <DialogTitle disableTypography className={classes.titleRoot}>
        <Typography variant="h6" className={classes.title}>
          {isWhoStep ? 'Add an admin' : 'What can they access?'}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
          {isWhoStep
            ? 'Give someone already in your company admin access, or invite someone new by email.'
            : 'Pick a role for each module. Leave a module on No access to keep them out of it.'}
        </Typography>
      </DialogTitle>

      <FormProvider {...dialog.methods}>
        <form onSubmit={event => event.preventDefault()}>
          <DialogContent className={classes.content}>
            {isWhoStep && !!companies?.length && (
              <Box marginBottom={2}>
                <CompanyFilter
                  companies={companies}
                  onCompanyChange={(event: unknown, company: Company | null) =>
                    dialog.setCompanyId(company?.uuid ?? null)
                  }
                />
              </Box>
            )}

            {/* Step 1 stays mounted behind step 2 so Back is instant and loses nothing */}
            <Box hidden={!isWhoStep}>
              <AdminWhoStep dialog={dialog} />
            </Box>

            {!isWhoStep && <AdminRoleStep dialog={dialog} />}
          </DialogContent>
        </form>
      </FormProvider>

      <DialogActions className={classes.actions}>
        <Typography className={classes.footerText}>
          {isWhoStep ? (
            (dialog.isExisting ? dialog.pickedUser : dialog.email) ? (
              <>
                <span className={classes.footerStrong}>1</span> ready
              </>
            ) : (
              'No one selected yet'
            )
          ) : (
            <>
              <span className={classes.footerStrong}>{dialog.assignedCount}</span>{' '}
              {dialog.assignedCount === 1 ? 'module' : 'modules'} with access
            </>
          )}
        </Typography>

        <Box display="flex" gridGap={8}>
          {isWhoStep ? (
            <>
              <AppButton size="large" variant="outlined" onClick={dialog.handleClose}>
                Cancel
              </AppButton>
              <AppButton
                size="large"
                variant="contained"
                color="primary"
                loading={dialog.checkingStaff || dialog.modulesStatus === 'loading'}
                loaderVariant="paper"
                disabled={!dialog.canGoNext}
                onClick={dialog.goNext}
              >
                Next
              </AppButton>
            </>
          ) : (
            <>
              <AppButton size="large" variant="outlined" onClick={dialog.goBack} disabled={dialog.saving}>
                Back
              </AppButton>
              <AppButton
                size="large"
                variant="contained"
                color="primary"
                loading={dialog.saving}
                loaderVariant="paper"
                disabled={!dialog.canSubmit}
                onClick={dialog.submit}
              >
                {submitLabel}
              </AppButton>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
